import { parse, differenceInMinutes } from 'date-fns';
import { buildVisitScheduleItem } from './buildVisitScheduleItem';

/**
 * Agrupa e funde eventos de prescrição com base na diferença máxima de tempo.
 * Eventos próximos acumulam seus `padPrescriptionIds` no mesmo objeto de visita.
 *
 * @param {Object[]} events - Array de eventos (allDateTimePrescriptionEvents)
 * @param {number} [maxTimeDifferenceMinutes=60] - Limite em minutos para agrupar na mesma visita
 * @returns {Map} Map onde a chave é uma string única e o valor é o objeto de visita unificado
 */
export function applyGroupEventsByTimeGapRules(events, maxTimeDifferenceMinutes = 60) {
  // Validação rápida da entrada
  if (!Array.isArray(events) || events.length === 0) {
    return new Map();
  }

  // Helper: converte {date, time} → Date
  const toDate = ({ date, time }) => {
    const [day, month, year] = date.trim().split('/');
    const isoString = `${year}-${month}-${day}T${time.trim()}:00`;
    const parsed = parse(isoString);

    if (isNaN(parsed.getTime())) {
      throw new Error(`Data/hora inválida: ${date} ${time}`);
    }
    return parsed;
  };

  // 1. Ordena os eventos cronologicamente para que a busca por proximidade funcione
  const sortedEvents = [...events].sort((a, b) => toDate(a) - toDate(b));

  const scheduleMap = new Map();

  // Array auxiliar para sabermos quais chaves/visitas já criamos e comparar os tempos
  const createdVisitsTimeline = [];

  // 2. Percorre os eventos ordenados
  // eslint-disable-next-line no-unused-vars
  for (const currentEvent of sortedEvents) {
    const { date, time, padPrescriptionId, padPrescription } = currentEvent;
    const currentEventDate = toDate(currentEvent);

    const { medicament, diet, procedure } = padPrescription;

    let matchedVisitKey = null;

    // Verifica se este evento se encaixa em alguma visita já criada na timeline
    // Como está ordenado, geralmente fará sentido comparar com os últimos criados
    for (let i = createdVisitsTimeline.length - 1; i >= 0; i--) {
      const existingVisit = createdVisitsTimeline[i];
      const diff = Math.abs(differenceInMinutes(currentEventDate, existingVisit.dateTime));

      if (diff <= maxTimeDifferenceMinutes) {
        matchedVisitKey = existingVisit.uniqueVisitKey;
        break; // Encontrou a visita correspondente dentro da janela de tempo
      }
    }

    if (matchedVisitKey) {
      // Se encontrou uma visita próxima, apenas adiciona o ID do medicamento a ela
      const visit = scheduleMap.get(matchedVisitKey);
      visit.padPrescriptionIds.push(padPrescriptionId);
      visit.padPrescriptionEvents.push({
        padPrescriptionId,
        date,
        time,
        medicament,
        diet,
        procedure
      });
    } else {
      // Se não encontrou nenhuma visita no limite de tempo, cria uma NOVA visita
      const uniqueVisitKey = `${date.replace(/\//g, '-')}-${time.replace(/:/g, '')}`;

      const newItem = buildVisitScheduleItem(currentEvent);

      scheduleMap.set(uniqueVisitKey, {
        ...newItem,
        uniqueVisitKey,
        padPrescriptionIds: [padPrescriptionId],
        padPrescriptionEvents: [{ padPrescriptionId, date, time, medicament, diet, procedure }]
      });

      // Salva na timeline para comparar com os próximos eventos do loop
      createdVisitsTimeline.push({
        uniqueVisitKey,
        dateTime: currentEventDate
      });
    }
  }

  return scheduleMap;
}
